import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { checkDailyLimits } from '../services/geminiService';
import { logger } from '../utils/secureLogger';

export interface DailyLimits {
  photoUpload: {
    allowed: boolean;
    remaining: number;
    limit: number;
    currentCount: number;
  };
}

export type ActionType = 'photoUpload';

export function useDailyLimits() {
  const { currentUser } = useAuth();
  const [limits, setLimits] = useState<DailyLimits | null>(null);
  const [loading, setLoading] = useState(true);

  // サーバーから制限情報を取得
  const fetchLimits = useCallback(async () => {
    if (!currentUser) {
      setLimits(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      logger.debug('Fetching daily limits from server');
      const serverLimits = await checkDailyLimits();
      setLimits(serverLimits);
      logger.debug('Daily limits updated:', serverLimits);
    } catch (error) {
      logger.error('Error fetching daily limits:', error);
      // エラー時はデフォルト値を設定
      setLimits({
        photoUpload: { allowed: true, remaining: 10, limit: 10, currentCount: 0 }
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // 初回取得
  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  // グローバルな使用回数更新イベントを監視
  useEffect(() => {
    const handleUsageUpdate = () => {
      fetchLimits();
    };

    window.addEventListener('dailyUsageUpdated', handleUsageUpdate);
    return () => {
      window.removeEventListener('dailyUsageUpdated', handleUsageUpdate);
    };
  }, [fetchLimits]);

  // 使用回数をリフレッシュ（サーバーサイドで自動管理されるため、取得のみ）
  const recordUsage = useCallback(async (): Promise<boolean> => {
    // サーバーサイドで使用回数が自動的に管理されるため、
    // フロントエンドからは最新の制限情報を取得するだけ
    await fetchLimits();
    
    // グローバルイベントを発火して他のコンポーネントにも通知
    window.dispatchEvent(new CustomEvent('dailyUsageUpdated'));
    
    return true;
  }, [fetchLimits]);

  // 残り回数を取得
  const getRemaining = useCallback((actionType: ActionType): number => {
    if (!limits) return 0;
    return limits[actionType].remaining;
  }, [limits]);

  // 制限チェック
  const checkLimit = useCallback((actionType: ActionType): boolean => {
    if (!limits) return true; // ローディング中は制限ありとして扱う
    return !limits[actionType].allowed;
  }, [limits]);

  // 表示用フォーマット
  const formatDisplay = useCallback((actionType: ActionType): string => {
    if (!limits) return '0/0';
    const limit = limits[actionType];
    return `${limit.currentCount}/${limit.limit}`;
  }, [limits]);

  // 手動リフレッシュ
  const refreshUsage = useCallback(() => {
    fetchLimits();
  }, [fetchLimits]);

  return {
    limits,
    loading,
    recordUsage,
    getRemaining,
    checkLimit,
    formatDisplay,
    refreshUsage,
    isLoggedIn: !!currentUser,
    
    // 後方互換性のために既存のプロパティも提供
    usage: limits ? {
      userId: currentUser?.id || '',
      date: new Date().toISOString().split('T')[0],
      photoUploads: limits.photoUpload.currentCount,
      lastUpdated: new Date()
    } : null,
  };
}
